<script setup lang="ts">
    import SortFormatRow, { type FormatChange } from '@/apps/ens/components/SortFormatRow.vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { getDisplaySequenceChanges, sortFormatsForDisplay, type DisplaySequenceChange } from '@/apps/ens/helpers.ts';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import _ from 'lodash';
    import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';

    type Format = {
        name: string;
        description: string;
        displaySequenceNo: number | null;
    };

    type DropTarget
        = { list: 'ordered'; index: number }
            | { list: 'unordered' };

    const props = defineProps<{
        ensType: string;
        facility: string;
        categoryDescription: string;
        formats: Format[];
        currentFormatName: string | null;
    }>();

    const emit = defineEmits<{
        close: [];
        submit: [changes: DisplaySequenceChange[]];
    }>();

    const sortedFormats = sortFormatsForDisplay(props.formats);
    const ordered = ref<Format[]>(sortedFormats.filter(format => format.displaySequenceNo !== null));
    const unordered = ref<Format[]>(sortedFormats.filter(format => format.displaySequenceNo === null));

    const orderedList = useTemplateRef<HTMLElement>('orderedList');
    const unorderedList = useTemplateRef<HTMLElement>('unorderedList');
    const draggedName = ref<string | null>(null);
    const dropTarget = ref<DropTarget | null>(null);

    const changes = computed(() => getDisplaySequenceChanges(ordered.value, unordered.value));
    const newNumberByName = computed(() => new Map(changes.value.map(change => [change.name, change.displaySequenceNo])));

    const getChange = (format: Format): FormatChange | null => {
        if (!newNumberByName.value.has(format.name))
            return null;
        const oldNumber = format.displaySequenceNo;
        const newNumber = newNumberByName.value.get(format.name) ?? null;
        if (oldNumber === null)
            return { icon: 'plus', label: 'changed (new)' };
        const label = `changed (was ${oldNumber})`;
        if (newNumber === null)
            return { icon: 'minus', label };
        return { icon: newNumber < oldNumber ? 'arrow-up' : 'arrow-down', label };
    };

    const dropIndex = computed(() => dropTarget.value?.list === 'ordered' ? dropTarget.value.index : null);
    const isDroppingOnUnordered = computed(() => dropTarget.value?.list === 'unordered');

    // Each drop position is marked before the row at that index, except the end of the list, after the last row
    const getDropMarker = (index: number) => {
        if (dropIndex.value === index)
            return 'before';
        const isLast = index === ordered.value.length - 1;
        if (isLast && dropIndex.value === ordered.value.length)
            return 'after';
        return null;
    };

    const moveFormat = (name: string, target: DropTarget) => {
        const format = [...ordered.value, ...unordered.value].find(x => x.name === name);
        if (!format)
            return;

        const remainingOrdered = ordered.value.filter(x => x.name !== name);
        const remainingUnordered = unordered.value.filter(x => x.name !== name);

        if (target.list === 'unordered') {
            ordered.value = remainingOrdered;
            unordered.value = _.sortBy([...remainingUnordered, format], 'description');
            return;
        }

        const currentIndex = ordered.value.indexOf(format);
        const index = currentIndex !== -1 && currentIndex < target.index ? target.index - 1 : target.index;
        remainingOrdered.splice(index, 0, format);

        ordered.value = remainingOrdered;
        unordered.value = remainingUnordered;
    };

    // =================================================================================================================
    // == Drag and drop

    const getDropIndex = (event: DragEvent) => {
        const rows = [...orderedList.value?.querySelectorAll('[data-name]') ?? []];
        const index = rows.findIndex(row => {
            const rect = row.getBoundingClientRect();
            return event.clientY < rect.top + rect.height / 2;
        });

        return index === -1 ? rows.length : index;
    };

    const onDragStart = (event: DragEvent, name: string) => {
        draggedName.value = name;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', name);
        }
    };

    const onDragEnd = () => {
        draggedName.value = null;
        dropTarget.value = null;
    };

    const onDragOverOrdered = (event: DragEvent) => {
        if (!draggedName.value)
            return;
        dropTarget.value = { list: 'ordered', index: getDropIndex(event) };
    };

    const onDragOverUnordered = () => {
        if (!draggedName.value)
            return;
        dropTarget.value = { list: 'unordered' };
    };

    const onDragLeave = (event: DragEvent) => {
        const container = event.currentTarget as Element;
        if (!container.contains(event.relatedTarget as Node | null))
            dropTarget.value = null;
    };

    const onDrop = () => {
        if (draggedName.value && dropTarget.value)
            moveFormat(draggedName.value, dropTarget.value);
        onDragEnd();
    };

    // =================================================================================================================
    // == Keyboard

    // Moving down from the last ordered format removes it from the order, and moving up from any format in the
    // alphabetical not ordered list adds it to the end of the order.
    const getTargetAbove = (name: string): DropTarget | null => {
        const index = ordered.value.findIndex(format => format.name === name);

        if (index === -1)
            return { list: 'ordered', index: ordered.value.length };

        if (index === 0)
            return null;

        return { list: 'ordered', index: index - 1 };
    };

    const getTargetBelow = (name: string): DropTarget | null => {
        const index = ordered.value.findIndex(format => format.name === name);

        if (index === -1)
            return null;

        if (index === ordered.value.length - 1)
            return { list: 'unordered' };

        return { list: 'ordered', index: index + 2 };
    };

    // The row is rendered again after a move, possibly in the other list, so focus has to be restored
    const focusRow = (name: string) => {
        const selector = `[data-name="${CSS.escape(name)}"]`;
        const row = orderedList.value?.querySelector<HTMLElement>(selector)
            ?? unorderedList.value?.querySelector<HTMLElement>(selector);

        row?.focus();
    };

    // ModalBase opens the b-modal already active, so Buefy never moves focus into it. Focus the modal itself, as
    // Buefy does, so screen readers announce the dialog and Tab starts at the top of it.
    onMounted(async () => {
        await nextTick();
        orderedList.value?.closest<HTMLElement>('.modal')?.focus();
    });

    const onKeydownRow = async (event: KeyboardEvent, name: string) => {
        const hasModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
        if (hasModifier)
            return;

        let target: DropTarget | null;
        if (event.key === 'ArrowUp')
            target = getTargetAbove(name);
        else if (event.key === 'ArrowDown')
            target = getTargetBelow(name);
        else
            return;

        // Let the arrow key scroll the modal as usual when the format cannot move
        if (!target)
            return;

        event.preventDefault();
        moveFormat(name, target);

        await nextTick();
        focusRow(name);
    };

    // =================================================================================================================
    // == Event handlers

    const onClickSortAlphabetically = () => {
        ordered.value = _.sortBy(ordered.value, 'description');
    };

    const onClickAddAllToOrder = () => {
        ordered.value = [...ordered.value, ...unordered.value];
        unordered.value = [];
    };

    const onClose = () => {
        emit('close');
    };

    const onClickSave = () => {
        emit('submit', changes.value);
        emit('close');
    };
</script>

<template>
    <modal-base @on-cancel="onClose">
        <template #modal-header>
            Edit order – {{ categoryDescription }} ({{ facility }})
        </template>
        <template #modal-body>
            <p class="mb-3">
                Drag and drop formats, or select one and use the arrow keys, to change the order. Saving creates a
                release that updates the display sequence number of the changed formats.
            </p>
            <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
                <h3 class="title is-5 mb-0">
                    Ordered
                </h3>
                <b-button
                    icon-left="sort-alphabetical-ascending"
                    label="Sort alphabetically"
                    :disabled="ordered.length < 2"
                    @click="onClickSortAlphabetically" />
            </div>
            <div
                ref="orderedList"
                class="drop-zone"
                :class="{
                    'is-empty': ordered.length === 0,
                    'drop-into': ordered.length === 0 && dropIndex === 0
                }"
                data-testid="ordered-list"
                @dragover.prevent="onDragOverOrdered"
                @dragleave="onDragLeave"
                @drop.prevent="onDrop">
                <sort-format-row
                    v-for="(format, index) in ordered"
                    :key="format.name"
                    :data-name="format.name"
                    :description="format.description"
                    :display-sequence-no="index + 1"
                    :is-current="format.name === currentFormatName"
                    :change="getChange(format)"
                    :drop-marker="getDropMarker(index)"
                    @dragstart="onDragStart($event, format.name)"
                    @dragend="onDragEnd"
                    @keydown="onKeydownRow($event, format.name)" />
            </div>

            <div class="is-flex is-align-items-center is-justify-content-space-between mt-5 mb-1">
                <h3 class="title is-5 mb-0">
                    Not ordered
                </h3>
                <b-button
                    icon-left="playlist-plus"
                    label="Add all to order"
                    :disabled="unordered.length === 0"
                    @click="onClickAddAllToOrder" />
            </div>
            <p class="mb-2 has-text-grey">
                Shown last, alphabetically. Drag formats between the lists to add or remove them from the order.
            </p>
            <div
                ref="unorderedList"
                class="drop-zone"
                :class="{ 'is-empty': unordered.length === 0, 'drop-into': isDroppingOnUnordered }"
                data-testid="unordered-list"
                @dragover.prevent="onDragOverUnordered"
                @dragleave="onDragLeave"
                @drop.prevent="onDrop">
                <sort-format-row
                    v-for="format in unordered"
                    :key="format.name"
                    :data-name="format.name"
                    :description="format.description"
                    :display-sequence-no="null"
                    :is-current="format.name === currentFormatName"
                    :change="getChange(format)"
                    class="has-background-white-bis has-text-grey"
                    @dragstart="onDragStart($event, format.name)"
                    @dragend="onDragEnd"
                    @keydown="onKeydownRow($event, format.name)" />
            </div>
        </template>
        <template #modal-footer>
            <span class="mr-auto has-text-weight-semibold">
                {{ changes.length }} {{ changes.length === 1 ? 'format' : 'formats' }} will be updated in one release.
            </span>
            <div class="buttons">
                <b-button
                    label="Cancel"
                    @click="onClose" />
                <b-button
                    v-require-can-edit-code="{ libraryName: ensType, scopes: [facility] }"
                    label="Save order"
                    type="is-primary"
                    :disabled="changes.length === 0"
                    @click="onClickSave" />
            </div>
        </template>
    </modal-base>
</template>

<style scoped>
.drop-zone {
    --row-gap: 0.35rem;
    display: flex;
    flex-direction: column;
    gap: var(--row-gap);
    min-height: 2.5rem;
    border-radius: var(--bulma-radius);
}

.drop-zone.is-empty {
    border: 1px dashed var(--bulma-border);
}

.drop-zone.drop-into {
    outline: 2px dashed var(--eq-prominent);
    outline-offset: 2px;
}
</style>
